'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  useGlobalData,
  useUpdateGlobalData
} from '@/providers/GlobalDataProvider';
import { getNotificationUnreadCountsAction } from '@/server/actions/notifications/getUnreadCounts.action';
import {
  C_DEFAULT_NOTIFICATION_COUNTS,
  C_DEFAULT_NOTIFICATION_PREFERENCES
} from '@/ts/constants/C_Notifications';
import { E_NotificationCategories } from '@/ts/enums/E_NotificationCategories';
import { T_NotificationUnreadCounts } from '@/ts/types/T_NotificationUnreadCounts';

type T_NotificationCountsState = T_NotificationUnreadCounts;

export function useNotificationCounts() {
  const globalCounts = useGlobalData('notificationUnreadCounts');
  const globalPreferences = useGlobalData('notificationPreferences');
  const updateGlobalData = useUpdateGlobalData();

  const [state, setState] = useState<T_NotificationCountsState>(
    globalCounts ?? C_DEFAULT_NOTIFICATION_COUNTS
  );
  const [isLoading, setIsLoading] = useState(false);

  const effectivePreferences = useMemo(() => {
    if (globalPreferences?.categoryPreferences) {
      return {
        ...C_DEFAULT_NOTIFICATION_PREFERENCES,
        ...globalPreferences.categoryPreferences
      };
    }
    return C_DEFAULT_NOTIFICATION_PREFERENCES;
  }, [globalPreferences]);

  const filteredTotal = useMemo(() => {
    let total = 0;
    Object.values(E_NotificationCategories).forEach(category => {
      if (effectivePreferences[category]) {
        total += state.categories[category] || 0;
      }
    });
    return total;
  }, [state.categories, effectivePreferences]);

  const fetchCounts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getNotificationUnreadCountsAction();
      const nextState: T_NotificationCountsState = {
        categories: {
          ...C_DEFAULT_NOTIFICATION_COUNTS.categories,
          ...(response?.data?.categories ?? {})
        },
        total: response?.data?.total ?? 0
      };

      setState(nextState);
      updateGlobalData({ notificationUnreadCounts: nextState });
    } catch (error) {
      console.error('Failed to fetch unread notification counts', error);
    } finally {
      setIsLoading(false);
    }
  }, [updateGlobalData]);

  useEffect(() => {
    if (!globalCounts) {
      void fetchCounts();
    } else {
      setState(globalCounts);
    }
  }, [fetchCounts, globalCounts]);

  return {
    counts: state.categories,
    total: filteredTotal,
    isLoading,
    refreshCounts: fetchCounts
  };
}
